import { useEffect, useMemo, type PropsWithChildren } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { isAdminPreviewMode, parseOptionalBoolValue } from '../../utils/admin-preview';
import { canEditApprovalBeforeClose } from '../../utils/config-utils';
import { isPastLondon } from '../../utils/londonTime';
import {
  parseSelectedFormIdFromSearch,
  resolveSelectedFormId,
  setSelectedFormIdInSession,
} from '../../utils/form-id';

export default function AccountPage({ children }: PropsWithChildren) {
  const { data } = useFormData();
  const { formConfig } = useFormConfig();
  const location = useLocation();
  const navigate = useNavigate();

  const formCloseAt = (formConfig as any)?.formCloseAt ?? (data as any)?.formCloseAt;
  const strictDeadline =
    (formConfig as any)?.strictDeadline ?? (data as any)?.strictDeadline ?? false;
  const isExpired =
    Boolean(data?.expired) || (strictDeadline && isPastLondon(formCloseAt ?? undefined));
  const formState = (data as any)?.formState as string | undefined;
  const role = String(data?.role || '').toLowerCase();
  const isEditableRole = role === 'owner' || role === 'editor';
  const tenant = formConfig?.id || '';
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const forms = data?.forms || [];
  const hasSingleForm = forms.length === 1;
  const myFormsBasePath = tenant ? `/${tenant}` : '/';
  const myFormsPath = `${myFormsBasePath}?my_forms=1&admin=0`;
  const isOnTenantRootPath =
    location.pathname === myFormsBasePath || location.pathname === `${myFormsBasePath}/`;
  const isOnSharePath = tenant && location.pathname === `/${tenant}/share`;
  const requestedAdminView = parseOptionalBoolValue(
    new URLSearchParams(location?.search || '').get('admin'),
  );
  const requestedMyFormsView =
    parseOptionalBoolValue(new URLSearchParams(location?.search || '').get('my_forms')) === true;
  const isOnMyFormsPath =
    isOnTenantRootPath &&
    (requestedMyFormsView ||
      (!selectedFormId && requestedAdminView !== true));
  const isPreviewMode = isAdminPreviewMode({
    search: location?.search || '',
    selectedFormId,
    hasAdminAccess: data?.hasAdminAccess,
    role: data?.role,
    sessionRoles: data?.sessionRoles,
    forms: data?.forms,
    allowPendingAccessCheck: true,
  });
  const isEditableApprovalBeforeClose = canEditApprovalBeforeClose({
    approvalCloseOnQuarter: formConfig.approvalCloseOnQuarter,
    formState,
    role,
    pendingApprovalQuarter: (data as any)?.pendingApprovalQuarter,
  });
  const staffSection = formConfig.sections?.find(
    (section) =>
      section.slug === location.pathname.split('/')[2] &&
      section.slug !== 'approvals',
  );
  const canEditScsStaffSection = tenant === 'scs' && isEditableRole && Boolean(staffSection);
  const formIdSearchState = useMemo(
    () => parseSelectedFormIdFromSearch(location?.search || ''),
    [location?.search],
  );
  const summaryPath = tenant ? `/${tenant}/summary` : '/summary';

  useEffect(() => {
    if (!tenant || !formIdSearchState.hasFormIdParam) return;
    setSelectedFormIdInSession(tenant, formIdSearchState.selectedFormIdFromUrl);
    const nextSearch = formIdSearchState.cleanedSearch ? `?${formIdSearchState.cleanedSearch}` : '';
    if ((location?.search || '') !== nextSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [
    formIdSearchState.cleanedSearch,
    formIdSearchState.hasFormIdParam,
    formIdSearchState.selectedFormIdFromUrl,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    tenant,
  ]);

  if (!selectedFormId && tenant && !isOnMyFormsPath && !isOnSharePath) {
    return <Navigate to={myFormsPath} replace />;
  }

  if (
    isExpired &&
    tenant &&
    selectedFormId &&
    location.pathname !== `/${tenant}/summary` &&
    !isOnSharePath
  ) {
    return <Navigate to={summaryPath} replace />;
  }

  if (
    ((formState === 'approval' && !isEditableApprovalBeforeClose) ||
      formState === 'review' ||
      (formState === 'rejected' && !isEditableRole) ||
      (formState === 'active' && !isEditableRole && !isPreviewMode)) &&
    tenant &&
    location.pathname !== `/${tenant}/summary` &&
    !isOnSharePath &&
    !((formState === 'approval' || formState === 'review') && hasSingleForm && isOnMyFormsPath)
  ) {
    return <Navigate to={summaryPath} replace />;
  }

  if (data?.forms && data.forms.length > 1) return <>{children}</>;

  if (
    data?.submitted &&
    !canEditScsStaffSection &&
    location.pathname !== `/${tenant}/summary` &&
    !isOnSharePath &&
    !(hasSingleForm && isOnMyFormsPath)
  )
    return <Navigate to={summaryPath} replace />;

  return <>{children}</>;
}
